import { useLayoutEffect, useRef } from 'react';
import type { Key } from '@react-types/shared';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import {
  ADVANCED_AI_SEARCH_ITEM_KEY,
  ADVANCED_SEARCH_ITEM_KEY,
} from './constants';

type SelectionCollection = {
  size: number;
  getItem: (key: Key) => unknown;
};

type UseQuickSearchCompactSelectionSyncProps = {
  collection: SelectionCollection;
  focusedKey?: string | null;
  hasAdvancedAiSearchItem?: boolean;
  hasQueriesSettled: boolean;
  hasSearchQuery: boolean;
  items: QuickSearchResult[];
  searchQuery: string;
  selectListItemFromListKey: (listKey?: Key | null) => void;
};

/**
 * Selects the AdvancedSearchItem by default, preserving any result item
 * the user has explicitly navigated to.
 */
export function useQuickSearchCompactSelectionSync({
  collection,
  focusedKey,
  hasAdvancedAiSearchItem = false,
  hasQueriesSettled,
  hasSearchQuery,
  items,
  searchQuery,
  selectListItemFromListKey,
}: UseQuickSearchCompactSelectionSyncProps) {
  const previousItemsRef = useRef(items);
  const previousSearchQueryRef = useRef(searchQuery);

  useLayoutEffect(() => {
    if (!hasSearchQuery) return;

    const previousItems = previousItemsRef.current;
    const previousSearchQuery = previousSearchQueryRef.current;
    previousItemsRef.current = items;
    previousSearchQueryRef.current = searchQuery;

    // A new query reuses the same (empty) items reference while its results
    // are loading, so a query change must force re-evaluation even when the
    // items reference is unchanged. Otherwise the AI item selected for a
    // prior no-match query stays focused until the new results resolve.
    const hasQueryChanged = searchQuery !== previousSearchQuery;

    if (previousItems === items && !hasQueryChanged && focusedKey != null) {
      return;
    }

    let nextSelectionKey: Key | null = null;

    if (
      focusedKey &&
      focusedKey !== ADVANCED_AI_SEARCH_ITEM_KEY &&
      collection.size > 0 &&
      collection.getItem(focusedKey as Key)
    ) {
      nextSelectionKey = focusedKey as Key;
    }

    // immediately switch to the AdvancedSearchItem if the AI item was focused
    // and the query has changed (the queries are settled because the new query
    // has has not been triggered yet due to debounce)
    if (focusedKey === ADVANCED_AI_SEARCH_ITEM_KEY && hasQueryChanged) {
      nextSelectionKey = ADVANCED_SEARCH_ITEM_KEY;
    }

    if (!nextSelectionKey) {
      // Only treat this as a confirmed no-name-match (and default to the AI
      // item) once the current query's results have settled. While results
      // are still loading, default to the AdvancedSearchItem.
      const isConfirmedNoNameMatch = items.length === 0 && hasQueriesSettled;
      nextSelectionKey = (
        hasAdvancedAiSearchItem && isConfirmedNoNameMatch
          ? ADVANCED_AI_SEARCH_ITEM_KEY
          : ADVANCED_SEARCH_ITEM_KEY
      ) as Key;
    }

    if (nextSelectionKey) selectListItemFromListKey(nextSelectionKey);
  }, [
    collection,
    focusedKey,
    hasAdvancedAiSearchItem,
    hasQueriesSettled,
    hasSearchQuery,
    items,
    searchQuery,
    selectListItemFromListKey,
  ]);
}
